import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import channelsBannerImgSrc from '../../../assets/images/appsStoreChannelsBanner.jpg';
import {ApplicationCardSkeleton} from '../../../components/common/applications/skeleton/applicationCardSkeleton';
import {IntegrationTypeModel} from '../../../models/api/integrationTypesModel';
import {SettingsAppsChannelsUrl} from '../../../models/url/settingsUrlModel';
import {AppsStoreBanner} from '../banner/appsStoreBanner';
import {AppsGrid} from '../layout/appsGrid';
import {SettingsAppCardContainer} from '../settingsAppCardContainer';

/*
 * Constants.
 */

const maxIntegrationsToRender = 4;

/*
 * Props.
 */

interface SettingsAppsChannelsPageProps {
  channelIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  popularChannelIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  recentlyAddedChannelIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  isIntegrationTypesLoading: boolean;
  url: SettingsAppsChannelsUrl;
}

/*
 * Component.
 */

export const SettingsAppsChannelsPage: FC<SettingsAppsChannelsPageProps> = (props) => {
  const {
    channelIntegrationTypes,
    popularChannelIntegrationTypes,
    recentlyAddedChannelIntegrationTypes,
    isIntegrationTypesLoading,
    url,
  } = props;

  return (
    <>
      <AppsStoreBanner
        backgroundUrl={channelsBannerImgSrc}
        title={<Trans id="settings-apps-channels-banner-title">Reach your customers anywhere</Trans>}
        description={
          <Trans id="settings-apps-channels-banner-description">
            From live chat to SMS to social platforms, Front’s library of channels makes it easy to send and
            receive messages with any customer, no matter where they might be reaching out from.
          </Trans>
        }
      />
      <AppsGrid
        title={<Trans id="settings-apps-channels-popular-title">Popular</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderSingleLineLoadingState}
        shouldHideOverflowingItems
      >
        {popularChannelIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={<Trans id="settings-apps-channels-recently-added-title">Recently added</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderSingleLineLoadingState}
        shouldHideOverflowingItems
      >
        {recentlyAddedChannelIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={<Trans id="settings-apps-channels-all-title">All</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderLoadingState}
      >
        {channelIntegrationTypes.map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
    </>
  );
};

/*
 * Helpers.
 */

function renderSingleLineLoadingState() {
  return (
    <>
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
    </>
  );
}

function renderLoadingState() {
  return (
    <>
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
    </>
  );
}
